import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Smartphone, Monitor, Globe, TrendingUp, TrendingDown,
  Users, Clock, MousePointer, AlertTriangle, CheckCircle,
  Info, Zap, ChevronDown, ChevronUp, RefreshCw, X,
  BarChart3, PieChart, Activity
} from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple chart components
const MiniBarChart = ({ data, dataKey, color = '#10b981', height = 60 }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d[dataKey] || 0));
  
  return (
    <div className="flex items-end gap-1 h-[60px]">
      {data.map((item, i) => (
        <div key={i} className="flex-1 flex flex-col items-center">
          <div
            className="w-full rounded-t transition-all duration-300"
            style={{
              height: `${max > 0 ? ((item[dataKey] || 0) / max) * height : 0}px`,
              backgroundColor: color,
              minHeight: item[dataKey] > 0 ? '4px' : '0'
            }}
          />
          <span className="text-[10px] text-gray-500 mt-1">
            {item.date?.slice(-2)}
          </span>
        </div>
      ))}
    </div>
  );
};

const DonutChart = ({ data, colors = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6'] }) => {
  if (!data || Object.keys(data).length === 0) return null;
  
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) return <div className="text-center text-gray-400 py-4">Sin datos</div>;
  
  let cumulativePercent = 0;
  const entries = Object.entries(data).filter(([_, v]) => v > 0);
  
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-24 h-24">
        <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
          {entries.map(([key, value], i) => {
            const percent = (value / total) * 100;
            const dashArray = `${percent} ${100 - percent}`;
            const dashOffset = -cumulativePercent;
            cumulativePercent += percent;
            
            return (
              <circle
                key={key}
                cx="18"
                cy="18"
                r="15.9155"
                fill="transparent"
                stroke={colors[i % colors.length]}
                strokeWidth="3"
                strokeDasharray={dashArray}
                strokeDashoffset={dashOffset}
                className="transition-all duration-500"
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold">{total}</span>
        </div>
      </div>
      <div className="flex-1 space-y-1">
        {entries.map(([key, value], i) => (
          <div key={key} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded"
              style={{ backgroundColor: colors[i % colors.length] }}
            />
            <span className="text-gray-400 capitalize">{key}</span>
            <span className="ml-auto font-medium">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const InsightCard = ({ insight, onDismiss }) => {
  const icons = {
    success: <CheckCircle className="w-5 h-5 text-emerald-400" />,
    warning: <AlertTriangle className="w-5 h-5 text-amber-400" />,
    action: <Zap className="w-5 h-5 text-blue-400" />,
    info: <Info className="w-5 h-5 text-gray-400" />
  };
  
  const colors = {
    success: 'border-emerald-500/30 bg-emerald-500/10',
    warning: 'border-amber-500/30 bg-amber-500/10',
    action: 'border-blue-500/30 bg-blue-500/10',
    info: 'border-gray-500/30 bg-gray-500/10'
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`p-4 rounded-xl border ${colors[insight.type]} relative`}
    >
      <button
        onClick={() => onDismiss(insight.id)}
        className="absolute top-2 right-2 text-gray-500 hover:text-gray-300"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        {icons[insight.type]}
        <div className="flex-1">
          <h4 className="font-medium text-white">{insight.title}</h4>
          <p className="text-sm text-gray-400 mt-1">{insight.message}</p>
          {insight.recommendation && (
            <p className="text-sm text-blue-400 mt-2 flex items-center gap-1">
              <Zap className="w-3 h-3" />
              {insight.recommendation}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const PWAAnalyticsDashboard = ({ adminId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7);
  const [showAllInsights, setShowAllInsights] = useState(false);
  
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/analytics/pwa/dashboard?days=${period}`, {
        headers: { 'X-Admin-Id': adminId }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching PWA analytics:', error);
      toast.error('Error cargando analytics de PWA');
    } finally {
      setLoading(false);
    }
  }, [adminId, period]);
  
  useEffect(() => {
    if (adminId) {
      fetchData();
    }
  }, [adminId, fetchData]);
  
  const dismissInsight = async (insightId) => {
    try {
      await axios.post(`${API_URL}/api/analytics/pwa/insights/${insightId}/dismiss`, {}, {
        headers: { 'X-Admin-Id': adminId }
      });
      setData(prev => ({
        ...prev,
        insights: prev.insights.filter(i => i.id !== insightId)
      }));
    } catch (error) {
      console.error('Error dismissing insight:', error);
    }
  };
  
  const formatDuration = (seconds) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="text-center py-12 text-gray-400">
        <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No hay datos de analytics disponibles</p>
      </div>
    );
  }
  
  const displayedInsights = showAllInsights ? data.insights : data.insights?.slice(0, 3);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Smartphone className="w-6 h-6 text-emerald-400" />
            PWA Analytics
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Métricas de uso y engagement de la aplicación
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
          >
            <option value={7}>Últimos 7 días</option>
            <option value={14}>Últimos 14 días</option>
            <option value={30}>Últimos 30 días</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="border-gray-700"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Insights Section */}
      {data.insights && data.insights.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-400" />
            Insights y Recomendaciones
          </h3>
          <div className="grid gap-3">
            {displayedInsights.map(insight => (
              <InsightCard
                key={insight.id}
                insight={insight}
                onDismiss={dismissInsight}
              />
            ))}
          </div>
          {data.insights.length > 3 && (
            <button
              onClick={() => setShowAllInsights(!showAllInsights)}
              className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
            >
              {showAllInsights ? (
                <>Ver menos <ChevronUp className="w-4 h-4" /></>
              ) : (
                <>Ver {data.insights.length - 3} más <ChevronDown className="w-4 h-4" /></>
              )}
            </button>
          )}
        </div>
      )}
      
      {/* Main Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* PWA vs Web */}
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">PWA vs Web</span>
            <Smartphone className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-white">{data.pwa_percentage}%</span>
            <span className="text-emerald-400 text-sm">PWA</span>
          </div>
          <div className="flex gap-1 mt-2">
            <div
              className="h-2 bg-emerald-500 rounded-l"
              style={{ width: `${data.pwa_percentage}%` }}
            />
            <div
              className="h-2 bg-blue-500 rounded-r"
              style={{ width: `${100 - data.pwa_percentage}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span>PWA: {data.pwa_users}</span>
            <span>Web: {data.web_users}</span>
          </div>
        </div>
        
        {/* Total Sessions */}
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Sesiones</span>
            <Users className="w-4 h-4 text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-white">{data.total_sessions}</p>
          <p className="text-xs text-gray-500 mt-1">
            {data.total_users} usuarios únicos
          </p>
          <div className="mt-2">
            <MiniBarChart
              data={data.sessions_by_day}
              dataKey="sessions"
              color="#3b82f6"
            />
          </div>
        </div>
        
        {/* Avg Session Duration */}
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Duración Media</span>
            <Clock className="w-4 h-4 text-purple-400" />
          </div>
          <p className="text-2xl font-bold text-white">
            {formatDuration(data.avg_session_duration_seconds)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {data.avg_pages_per_session?.toFixed(1)} páginas/sesión
          </p>
        </div>
        
        {/* Bounce Rate */}
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Tasa de Rebote</span>
            {data.bounce_rate > 60 ? (
              <TrendingDown className="w-4 h-4 text-red-400" />
            ) : (
              <TrendingUp className="w-4 h-4 text-emerald-400" />
            )}
          </div>
          <p className="text-2xl font-bold text-white">{data.bounce_rate}%</p>
          <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
            <div
              className={`h-2 rounded-full transition-all ${
                data.bounce_rate > 60 ? 'bg-red-500' : 
                data.bounce_rate > 40 ? 'bg-amber-500' : 'bg-emerald-500'
              }`}
              style={{ width: `${Math.min(data.bounce_rate, 100)}%` }}
            />
          </div>
        </div>
      </div>
      
      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* PWA vs Web Trend */}
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-400" />
            PWA vs Web (Últimos {period} días)
          </h3>
          <div className="space-y-3">
            {data.pwa_vs_web_by_day?.map((day, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-10">{day.date?.slice(-5)}</span>
                <div className="flex-1 flex gap-1 h-4">
                  <div
                    className="bg-emerald-500 rounded-l h-full transition-all"
                    style={{ width: `${(day.pwa / (day.pwa + day.web || 1)) * 100}%` }}
                  />
                  <div
                    className="bg-blue-500 rounded-r h-full transition-all"
                    style={{ width: `${(day.web / (day.pwa + day.web || 1)) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-16 text-right">
                  {day.pwa + day.web}
                </span>
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 bg-emerald-500 rounded" />
              <span className="text-gray-400">PWA</span>
            </span>
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded" />
              <span className="text-gray-400">Web</span>
            </span>
          </div>
        </div>
        
        {/* Device Breakdown */}
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-purple-400" />
            Dispositivos
          </h3>
          <DonutChart
            data={data.device_breakdown}
            colors={['#10b981', '#3b82f6', '#f59e0b']}
          />
        </div>
      </div>
      
      {/* Bottom Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <MousePointer className="w-5 h-5 text-blue-400" />
            Páginas más visitadas
          </h3>
          <div className="space-y-2">
            {data.top_pages?.slice(0, 5).map((page, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-gray-500 text-sm w-4">{i + 1}</span>
                <span className="flex-1 text-sm text-gray-300 truncate">
                  {page.page || '/'}
                </span>
                <span className="text-sm font-medium text-white">{page.views}</span>
              </div>
            ))}
            {(!data.top_pages || data.top_pages.length === 0) && (
              <p className="text-center text-gray-500 py-4">Sin datos</p>
            )}
          </div>
        </div>
        
        {/* Conversion Funnel */}
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-amber-400" />
            Embudo de Conversión
          </h3>
          <div className="space-y-3">
            {[
              { label: 'Visitas', value: data.funnel_data?.page_views || 0, color: 'bg-blue-500' },
              { label: 'Ven Kupones', value: data.funnel_data?.kupon_views || 0, color: 'bg-purple-500' },
              { label: 'Reservas', value: data.funnel_data?.reservations || 0, color: 'bg-amber-500' },
              { label: 'Canjes', value: data.funnel_data?.redemptions || 0, color: 'bg-emerald-500' }
            ].map((step, i) => {
              const maxVal = data.funnel_data?.page_views || 1;
              const width = (step.value / maxVal) * 100;
              return (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">{step.label}</span>
                    <span className="text-white font-medium">{step.value}</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${step.color} transition-all duration-500`}
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Browser & OS Breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-cyan-400" />
            Navegadores
          </h3>
          <DonutChart
            data={data.browser_breakdown}
            colors={['#06b6d4', '#8b5cf6', '#f59e0b', '#ec4899']}
          />
        </div>
        
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Monitor className="w-5 h-5 text-pink-400" />
            Sistemas Operativos
          </h3>
          <DonutChart
            data={data.os_breakdown}
            colors={['#ec4899', '#10b981', '#3b82f6', '#f59e0b']}
          />
        </div>
      </div>
    </div>
  );
};

export default PWAAnalyticsDashboard;
